/**
 * ModuleListV2 — wspólny szkielet modułów hodowli w UI 2.0 (Suki / Reproduktory / Mioty).
 *
 * Layout: StudioShell (LEWE MENU) + jasny content area (premium UI 2.0).
 * Każdy moduł: Lista · Pusty stan · Dodaj · (na karcie) Edytuj · Usuń · Podgląd.
 *
 * AKTUALNE (UI 2.0). Zastępuje legacy ProfilePanel (zakładki) jako docelowy widok modułów.
 */
import { useState } from 'react';
import StudioShell from '@/components/studio/StudioShell';
import {
  Plus, Loader2, MoreVertical, Pencil, Eye, Trash2, ImageOff,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';

/* ───────────────── Shell + header + stany ───────────────── */
export function ModuleListV2({
  activeKey, contextLabel, icon: Icon, title, subtitle,
  addLabel, onAdd, loading, items, empty, children,
}) {
  const hasItems = Array.isArray(items) && items.length > 0;
  return (
    <StudioShell mode="workspace" contextLabel={contextLabel} activeKey={activeKey}>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 lg:py-8" data-testid={`module-${activeKey}`}>
        {/* Nagłówek */}
        <div className="flex items-start justify-between gap-4 mb-7">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-2xl bg-orange-50 flex items-center justify-center flex-shrink-0">
              <Icon className="w-5 h-5 text-orange-500" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900 leading-tight truncate">{title}</h1>
              <p className="text-sm text-zinc-500 mt-0.5">{subtitle}</p>
            </div>
          </div>
          {hasItems && (
            <button
              onClick={onAdd}
              data-testid={`module-${activeKey}-add`}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm shadow-[0_10px_24px_-8px_rgba(249,115,22,0.6)] hover:-translate-y-0.5 transition-all flex-shrink-0"
            >
              <Plus className="w-4 h-4" /> {addLabel}
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-32" data-testid={`module-${activeKey}-loading`}>
            <Loader2 className="w-7 h-7 animate-spin text-orange-500" />
          </div>
        ) : !hasItems ? (
          <EmptyState activeKey={activeKey} empty={empty} onAdd={onAdd} addLabel={addLabel} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid={`module-${activeKey}-grid`}>
            {children}
          </div>
        )}
      </div>
    </StudioShell>
  );
}

function EmptyState({ activeKey, empty, onAdd, addLabel }) {
  const Icon = empty.icon;
  return (
    <div
      className="rounded-3xl border-2 border-dashed border-zinc-200 bg-white/60 py-20 px-6 flex flex-col items-center text-center"
      data-testid={`module-${activeKey}-empty`}
    >
      <div className="w-16 h-16 rounded-2xl bg-orange-50 flex items-center justify-center mb-5">
        <Icon className="w-8 h-8 text-orange-400" />
      </div>
      <h2 className="text-xl font-bold text-zinc-900 mb-2">{empty.title}</h2>
      <p className="text-sm text-zinc-500 max-w-md mb-7 leading-relaxed">{empty.text}</p>
      <button
        onClick={onAdd}
        data-testid={`module-${activeKey}-empty-add`}
        className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm shadow-[0_12px_28px_-8px_rgba(249,115,22,0.6)] hover:-translate-y-0.5 transition-all"
      >
        <Plus className="w-4 h-4" /> {addLabel}
      </button>
    </div>
  );
}

/* ───────────────── Karta pozycji ───────────────── */
export function ModuleCardV2({ id, image, title, subtitle, badges = [], onEdit, onPreview, onDelete }) {
  return (
    <div
      className="group relative rounded-2xl border border-zinc-200 bg-white overflow-hidden hover:shadow-[0_18px_40px_-18px_rgba(0,0,0,0.18)] hover:border-orange-200 transition-all"
      data-testid={`module-card-${id}`}
    >
      {/* Zdjęcie */}
      <div className="relative aspect-[4/3] bg-zinc-100 overflow-hidden">
        {image ? (
          <img src={image} alt={title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-300">
            <ImageOff className="w-10 h-10" />
          </div>
        )}
        {/* Menu akcji */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid={`module-card-${id}-menu`}
              className="absolute top-2.5 right-2.5 w-9 h-9 rounded-lg bg-white/90 backdrop-blur flex items-center justify-center text-zinc-600 hover:text-zinc-900 shadow-sm hover:bg-white transition-colors"
            >
              <MoreVertical className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={onEdit} data-testid={`module-card-${id}-edit`} className="cursor-pointer">
              <Pencil className="w-4 h-4 mr-2" /> Edytuj
            </DropdownMenuItem>
            {onPreview && (
              <DropdownMenuItem onClick={onPreview} data-testid={`module-card-${id}-preview`} className="cursor-pointer">
                <Eye className="w-4 h-4 mr-2" /> Podgląd
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={onDelete} data-testid={`module-card-${id}-delete`} className="cursor-pointer text-red-600 focus:text-red-600">
              <Trash2 className="w-4 h-4 mr-2" /> Usuń
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Treść */}
      <div className="p-4">
        <h3 className="text-base font-bold text-zinc-900 truncate">{title}</h3>
        {subtitle && <p className="text-sm text-zinc-500 mt-0.5 truncate">{subtitle}</p>}
        {badges.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {badges.map((b, i) => (
              <span
                key={i}
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${b.className || 'bg-zinc-100 text-zinc-600'}`}
              >
                {b.label}
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2 mt-4">
          <button
            onClick={onEdit}
            data-testid={`module-card-${id}-edit-btn`}
            className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-lg border border-zinc-200 text-zinc-700 hover:border-orange-300 hover:text-orange-600 text-sm font-medium transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" /> Edytuj
          </button>
          {onPreview && (
            <button
              onClick={onPreview}
              data-testid={`module-card-${id}-preview-btn`}
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-zinc-200 text-zinc-500 hover:border-orange-300 hover:text-orange-600 transition-colors"
              title="Podgląd"
            >
              <Eye className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ───────────────── Dialog potwierdzenia usunięcia ───────────────── */
export function ConfirmDeleteDialog({ open, onOpenChange, itemName, onConfirm, submitting }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="confirm-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Usunąć „{itemName}"?</AlertDialogTitle>
          <AlertDialogDescription>
            Tej operacji nie można cofnąć z poziomu panelu. Pozycja zniknie z Twojego profilu i katalogu.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-delete-cancel">Anuluj</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={submitting}
            data-testid="confirm-delete-confirm"
            className="bg-red-600 hover:bg-red-700"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Usuń'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/* Hook współdzielony: zarządzanie stanem usuwania pozycji. */
export function useModuleDelete(deleteFn, onDone) {
  const [target, setTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const confirm = async () => {
    if (!target) return;
    setSubmitting(true);
    try {
      await deleteFn(target);
      onDone?.();
    } finally {
      setSubmitting(false);
      setTarget(null);
    }
  };
  return {
    target, setTarget, submitting, confirm,
    dialogProps: {
      open: !!target,
      onOpenChange: (v) => { if (!v) setTarget(null); },
      itemName: target?.name || target?.title || 'pozycję',
      onConfirm: confirm,
      submitting,
    },
  };
}
